/**
 * The chat's version row as state: seeded from this browser's stored row (or the account's
 * start set), changed one code at a time, and handed to every ask. The pure rules it applies
 * are in `activeVersions.ts`; the row's drawing is `versionPicker.tsx`.
 */

import { useCallback, useEffect, useState } from 'react'
import type { Account } from './account'
import {
  addAnswered,
  addableOnly,
  changed,
  readStoredRow,
  replaced,
  rowForAsk,
  rowKey,
  seeded,
  startAdditions,
  UNKNOWN_ROW,
  writeStoredRow,
  type RowState,
} from './activeVersions'
import type { CorpusFacts } from './corpusFacts'
import type { PickableVersion } from './versionPicker'

/** The codes of the corpus's addable versions, and of the pinned ones among them. */
function codesOf(versions: PickableVersion[]): { addable: string[]; pinned: string[] } {
  return {
    addable: versions.map((version) => version.code),
    pinned: versions.filter((version) => version.pinned).map((version) => version.code),
  }
}

/**
 * The row's state, seeded and stored. In this order:
 *
 * 1. As soon as the corpus is known, the stored row — no account needed, so nothing the
 *    reader does in the meantime can overwrite a row this browser already holds.
 * 2. Only when nothing is stored AND the account has loaded, the start set's additions;
 *    a non-empty one is written to storage once, so the reading pages start from it too.
 * 3. A change before either (an add, an answer) waits in `pending` and applies after.
 *
 * Storage is written in an effect, never inside a state updater, and only for a row the
 * reader or the start set made — a row that came from storage is not written back.
 */
function useStoredRow(
  account: Account | null,
  accountKnown: boolean,
  corpusCode: string | null,
  pinned: string[],
): [RowState, React.Dispatch<React.SetStateAction<RowState>>] {
  const [state, setState] = useState<RowState>(UNKNOWN_ROW)
  const pinnedKey = pinned.join(' ')

  useEffect(() => {
    if (corpusCode === null) return
    const stored = readStoredRow(corpusCode)
    if (stored !== null) return setState((current) => seeded(current, stored, false))
    if (!accountKnown) return
    const start = startAdditions(account, corpusCode, pinnedKey.split(' '))
    setState((current) => seeded(current, start, start.length > 0))
  }, [account, accountKnown, corpusCode, pinnedKey])

  // Another tab (a reading page, say) changed the row, or cleared the site's storage: follow it.
  useEffect(() => {
    if (corpusCode === null) return
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== null && event.key !== rowKey(corpusCode)) return
      const stored = readStoredRow(corpusCode)
      // Cleared before this page knew its row: leave it to the seeding above.
      setState((current) =>
        stored === null && current.added === null ? current : replaced(current, stored ?? [], false),
      )
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [corpusCode])

  useEffect(() => {
    if (state.toStore && state.added !== null && corpusCode !== null) writeStoredRow(corpusCode, state.added)
  }, [state, corpusCode])

  return [state, setState]
}

/**
 * The version row: the pinned versions plus the ADDED codes, which live in this browser's
 * `sourcetext.versions.<corpus>` — the same key, in the same format, the reading pages use,
 * so both show one row (`useStoredRow` holds the seeding order). The row IS what the next
 * ask sends; null (the corpus description failed to load) lets the server pick, and the
 * server pins the original there too. `forAsk` is also what App provides as
 * `ActiveVersionsContext`, so a passage card shows the versions the next question reads.
 * Only `addable` versions count (`can_be_active` on the server — the predicate the reading
 * pages use too), so neither surface drops a code the other keeps. Nothing here writes
 * to the account.
 */
export function useSessionVersions(
  account: Account | null,
  accountKnown: boolean,
  facts: CorpusFacts,
): SessionVersions {
  const { addable, pinned } = codesOf(facts.versions)
  const [state, setState] = useStoredRow(account, accountKnown, facts.code, pinned)
  const addableKey = addable.join(' ')
  const pinnedKey = pinned.join(' ')

  // Every change goes through `changed`, an updater over the row as it is when it applies:
  // a ✕ pressed while an answer was written is not undone when it finishes. None is made
  // before the corpus is known: its filter would capture an empty addable list and, queued
  // until seeding, empty the stored row. (The row is not drawn then, so only an answer
  // finishing first can land here, and it adds nothing the next answer will not.)
  const change = useCallback(
    (edit: (added: string[]) => string[]) => {
      // No addable version means the corpus is not known yet (or offers nothing to add).
      if (addableKey === '') return
      setState((current) =>
        changed(current, (added) => edit(addableOnly(added, addableKey.split(' '), pinnedKey.split(' ')))),
      )
    },
    [setState, addableKey, pinnedKey],
  )
  // Applied to whatever the row holds by then, which may already have it (a pending change, a second press).
  const add = useCallback(
    (code: string) => change((added) => (added.includes(code) ? added : [...added, code])),
    [change],
  )
  const remove = useCallback((code: string) => change((added) => added.filter((held) => held !== code)), [change])
  // After an answer: any non-pinned version the server resolved or a tool read joins the row.
  const onAnswered = useCallback(
    (set: string[]) =>
      change((added) => addAnswered(added, set.filter((code) => addableKey.split(' ').includes(code)), pinnedKey.split(' '))),
    [change, addableKey, pinnedKey],
  )

  const row = rowForAsk(pinned, state.added ?? [], addable)
  const forAsk = facts.versions.length > 0 ? row : null
  return { added: row.slice(pinned.length), forAsk, add, remove, onAnswered, versions: facts.versions }
}

/** The row's added versions, how to change them, and every version the reader may add. */
export interface SessionVersions {
  added: string[]
  /** What the next ask sends: pinned + added; null when the corpus is unknown (server default). */
  forAsk: string[] | null
  add: (code: string) => void
  remove: (code: string) => void
  /** A finished answer's set (`setAfterAnswer`): its non-pinned codes join the row. */
  onAnswered: (set: string[]) => void
  versions: PickableVersion[]
}
